import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';

import { Button } from '@eventbrite/eds-button';
import { CardList } from '@eventbrite/eds-card-list';
import { STYLE_PROP_TYPE } from '@eventbrite/eds-card';
import { CARD_ITEMS_PROP_TYPE } from '@eventbrite/eds-card-list';
import { Icon } from '@eventbrite/eds-icon';

import './expandableCardContent.scss';

import { ChevronUpChunky } from '@eventbrite/eds-iconography';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';

const ExpandedItems = ({ isExpanded, items, style, onSortEnd, dragHandle }) => {
    let expandedItems = null;

    if (isExpanded) {
        expandedItems = (
            <div
                className="eds-expandable-card-content__expanded-items eds-fx--fade-in"
                data-spec="spec-expanded-items"
                data-automation="expandable-card-expanded-items"
            >
                <CardList
                    items={items}
                    style={style}
                    onSortEnd={onSortEnd}
                    dragHandle={dragHandle}
                />
            </div>
        );
    }

    return expandedItems;
};

export default class ExpandableCardContent extends PureComponent {
    static propTypes = {
        /**
         * List of items to render when the component is expanded
         */
        items: CARD_ITEMS_PROP_TYPE.isRequired,
        /**
         * The main content that is displayed
         */
        children: PropTypes.node.isRequired,
        /**
         * Style of nested items
         */
        style: STYLE_PROP_TYPE,
        /**
         * Whether or not the card is expanded initially
         */
        initiallyExpanded: PropTypes.bool,
        /**
         * Callback to be triggered when the content is expanded or collapsed
         * Ex. (isExpanded) => {};
         */
        onToggle: PropTypes.func,
        /**
         * Override the internal state of whether or not the content is expanded
         */
        isExpanded: PropTypes.bool,
        /**
         * Pass this function to make the expanded content items sortable.
         * We call it when sorting is completed with the oldIndex,
         * and newIndex of the dragged item, and a function named arrayMove.
         * Reorder your items by calling arrayMove(items, oldIndex, newIndex)
         * which will in turn re-render the UI. See Card List for details.
         */
        onSortEnd: PropTypes.func,
        /**
         * An element to use as the drag handle for each sortable item.
         * By default a sortable list gets a drag handle.
         * You can also pass in null, to make the entire item draggable.
         * Only applicable if onSortEnd was passed in.
         */
        dragHandle: PropTypes.node,
        /**
         * Optional id used to generate unique ids for icons.
         */
        id: PropTypes.string,
    };

    static defaultProps = {
        initiallyExpanded: false,
    };

    constructor(props) {
        super(props);

        const { initiallyExpanded, isExpanded } = props;

        this.state = {
            isExpanded:
                isExpanded === undefined ? initiallyExpanded : isExpanded,
        };
    }

    UNSAFE_componentWillReceiveProps({ isExpanded: nextIsExpanded }) {
        if (nextIsExpanded === this.props.isExpanded) {
            return;
        }

        this.setState({ isExpanded: nextIsExpanded });
    }

    _handleExpandToggle() {
        const { onToggle } = this.props;
        const { isExpanded: isPreviouslyExpanded } = this.state;
        const isExpanded = !isPreviouslyExpanded;

        this.setState({
            isExpanded,
        });

        if (onToggle) {
            onToggle(isExpanded);
        }
    }

    render() {
        const { children, items, style, onSortEnd, dragHandle, id } =
            this.props;
        const { isExpanded } = this.state;
        const iconType = isExpanded ? (
            <ChevronUpChunky
                {...(id
                    ? {
                          id: `chevron-up-chunky_svg__eds-icon--chevron-up-chunky_svg-${id}`,
                      }
                    : {})}
            />
        ) : (
            <ChevronDownChunky
                {...(id
                    ? {
                          id: `chevron-down-chunky_svg__eds-icon--chevron-down-chunky_svg-${id}`,
                      }
                    : {})}
            />
        );
        const mainClassName = classNames('eds-expandable-card-content__main', {
            'eds-expandable-card-content__main--expanded': isExpanded,
        });

        return (
            <div className="eds-expandable-card-content">
                <Button
                    style="none"
                    data-automation="eds-expandable-card-content-button"
                    onClick={this._handleExpandToggle.bind(this)}
                    __containerClassName={mainClassName}
                >
                    <div className="eds-expandable-card-content__main-content">
                        {children}
                    </div>

                    <div className="eds-expandable-card-content__toggle eds-l-pad-hor-4">
                        <Icon
                            type={iconType}
                            title={gettext('Toggle Content')}
                        />
                    </div>
                </Button>

                <ExpandedItems
                    isExpanded={isExpanded}
                    items={items}
                    style={style}
                    onSortEnd={onSortEnd}
                    dragHandle={dragHandle}
                />
            </div>
        );
    }
}
